import { useEffect, useState, useContext } from "react";
import { useNavigate, useSearchParams, useParams } from "react-router";
import axios from 'axios';
import { usePostHog } from '@posthog/react';

import { AppContext } from '../../context.js';

import { ChevronDown } from "../../files/icons/index.js";
import { Button } from '../components/component.js';
import { EditableDatePicker } from "../components/datePicker.js";
import MaskedInput from "../components/maskedInput.js";
import { ICD10_CODES } from "../components/icd10.js";
import { formatDateYMD } from "../../utils/datetime.js";
import { sanitizeMBI, isValidMBI } from "../../utils/mbi.js";
import { sanitizeZip, isAcceptablePatientZip, ZIP_HINT } from "../../utils/zip.js";
import { isRCCResidence, isPatientFieldMissing, MISSING_FIELD_CLASS } from "../../utils/paafRequired.js";

// Fields only relevant when the patient is an existing patient being migrated in;
// hidden until the "Existing patient" radio is set to "Yes" (create mode only —
// edit always shows them).
const EXISTING_PATIENT_GATED = ["alignmentDate", "icd10Code", "tierAssignment", "assessmentDate"];

// Post-Alignment (New) then Established G-code templates for the tierAssignment select.
function tierTemplatesFromSettings(settings) {
  const templates = settings?.projectTemplates || [];
  const groups = settings?.projectTemplatesGroups || [];
  const result = [];
  for (const phase of ["POST_NEW", "POST_EST"]) {
    const group = groups.find((g) => g.phase === phase);
    if (!group) continue;
    for (const id of group.templates || []) {
      const t = templates.find((x) => x.id === id);
      if (t) result.push(t);
    }
  }
  return result;
}

// Mirrors projects.GCodeFromTemplateName / taskCard gcodeFromTemplateTitle.
function gcodeFromTemplateTitle(title) {
  const match = (title || "").match(/G05\d{2}/i);
  return match ? match[0].toUpperCase() : "";
}

function firstTierGCode(settings) {
  for (const t of tierTemplatesFromSettings(settings)) {
    const code = gcodeFromTemplateTitle(t.title);
    if (code) return code;
  }
  return "";
}

const YMD_RE = /^\d{4}-\d{2}-\d{2}$/;

// EditableDatePicker stores JS Date objects; axios would serialize those as ISO
// UTC (shifting the calendar day). Convert date-type fields to local YYYY-MM-DD.
function normalizeFormDates(form, settings) {
  const dateCodes = new Set(
    (settings?.patientTemplate?.fields || [])
      .filter((f) => f.type === "date")
      .map((f) => f.code)
  );
  return Object.fromEntries(
    Object.entries(form).map(([k, v]) => {
      let value = typeof v === "string" ? v.trim() : v;
      if (dateCodes.has(k) && value != null && value !== "") {
        if (typeof value === "string" && YMD_RE.test(value)) {
          // already YYYY-MM-DD
        } else {
          const date = value instanceof Date ? value : new Date(value);
          if (!Number.isNaN(date.getTime())) {
            value = formatDateYMD(date);
          }
        }
      }
      return [k, value];
    })
  );
}

// Physical Address code -> Mailing Address code. Present only when the patient
// template has the split Physical/Mailing sections (see migration m27).
const PHYSICAL_TO_MAILING = {
  physicalAddress: "address",
  physicalCity: "city",
  physicalState: "state",
  physicalPostalCode: "postalCode",
};
const MAILING_ADDRESS_CODES = Object.values(PHYSICAL_TO_MAILING);
const POSTAL_CODE_FIELDS = ["postalCode", "physicalPostalCode"];

function isPostalCodeField(code) {
  return POSTAL_CODE_FIELDS.includes(code);
}

function PatientCreate() {
  const context = useContext(AppContext);
  const posthog = usePostHog();

  let { id } = useParams();
  let patientID = id;
  let createMode = id === "create";

  const navigate = useNavigate();

  // Get URL params
  const [searchParams, setSearchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);

  const [settings, setSettings] = useState(null);

  const [form, setForm] = useState({});
  const [formErrors, setFormErrors] = useState({});
  const [sameAsPhysical, setSameAsPhysical] = useState(false);
  // Set on edit load; create has no lifecycle yet.
  const [lifecycleStage, setLifecycleStage] = useState("");

  const [referralID, setReferralID] = useState(searchParams.get("referralID"));

  const isPostAlignment = lifecycleStage === "POST_ALIGNMENT_NEW" || lifecycleStage === "POST_ALIGNMENT_EST";
  // Post-alignment: alignmentDate is locked; tier stays required.
  // Create + Existing Yes: both required for migration.
  // Pre-alignment / unaligned / create+No: both may be null.
  const requiresTier = isPostAlignment || (createMode && form.existingPatient === "Yes");
  const requiresAlignment = createMode && form.existingPatient === "Yes";
  const alignmentDateLocked = isPostAlignment;

  useEffect(() => {
    if (patientID != "create") {
      let u = context.addSpiner();
      axios.all([
        axios.get('/api/admin/settings', {
        }),
        axios.post('/api/admin/patients', {
          filter: {
            id: [patientID],
          }
        })
      ]).then(axios.spread((settings, patients) => {
        let patient = patients.data.patients[0];

        // Populate patient data into form; prefer structured alignmentDate for
        // the alignment-date field so the picker matches lifecycle state.
        const newForm = {};
        patient.fields.forEach((field) => {
          if (field.answers && field.answers.length > 0) {
            newForm[field.code] = field.answers[0];
          }
        });
        if (patient.alignmentDate) {
          newForm.alignmentDate = patient.alignmentDate;
        }
        // Prefer structured assessmentDate (the PAAF cycle anchor) over the form
        // field mirror so the picker reflects the authoritative top-level value.
        if (patient.assessmentDate) {
          newForm.assessmentDate = patient.assessmentDate;
        }
        // Prefer structured procedureCode (set by the Tier Assignment task /
        // New→Est conversion). Normalize leftover template-title answers to a
        // bare G-code so the select value matches option values.
        if (patient.procedureCode) {
          newForm.tierAssignment = patient.procedureCode;
        } else if (newForm.tierAssignment) {
          const extracted = gcodeFromTemplateTitle(newForm.tierAssignment);
          if (extracted) {
            newForm.tierAssignment = extracted;
          }
        }
        // Prefer claimInfo.primaryDiagnosis (set by the ICD-10 task action) when
        // the icd10Code form answer is missing — same idea as tierAssignment.
        if (patient.claimInfo?.primaryDiagnosis && !newForm.icd10Code) {
          newForm.icd10Code = patient.claimInfo.primaryDiagnosis;
        }
        // Existing patient must be Yes or No — never null.
        if (
          settings.data.patientTemplate.fields.some((f) => f.code === "existingPatient") &&
          !newForm["existingPatient"]
        ) {
          newForm["existingPatient"] = "No";
        }
        setForm(newForm);
        setLifecycleStage(patient.lifecycleStage || "");

        // Auto-check "same as physical" when the mailing address already
        // matches the physical address (all pairs non-empty and equal).
        const allMatch = Object.entries(PHYSICAL_TO_MAILING).every(
          ([phys, mail]) => newForm[phys] && newForm[phys] === newForm[mail]
        );
        setSameAsPhysical(allMatch);

        setSettings(settings.data);
        setLoading(false);
      })).catch((e) => {
        context.addError(e);
        context.removeSpiner(u);
      }).finally(() => {
        context.removeSpiner(u);
      });
    } else if (referralID && referralID !== "") {
      let u = context.addSpiner();
      axios.all([
        axios.get('/api/admin/settings', {
        }),
        axios.post('/api/admin/referrals', {
          filter: {
            id: [referralID]
          }
        })
      ]).then(axios.spread((settings, referrals) => {
        const newForm = {};
        newForm["firstName"] = referrals.data.referrals[0].firstName;
        newForm["lastName"] = referrals.data.referrals[0].lastName;
        newForm["email"] = referrals.data.referrals[0].email;
        newForm["phone"] = referrals.data.referrals[0].phone;

        referrals.data.referrals[0].fields.forEach((field) => {
          if (field.answers && field.answers.length > 0) {
            newForm[field.code] = field.answers[0];
            //console.log("Field", field.code, "set to", field.answers[0]);
          }
        });

        // Default "Existing patient" to "No" when the template has the field.
        if (
          settings.data.patientTemplate.fields.some((f) => f.code === "existingPatient") &&
          !newForm["existingPatient"]
        ) {
          newForm["existingPatient"] = "No";
        }
        setForm(newForm);

        setSettings(settings.data);
        setLoading(false);
      })).catch((e) => {
        context.addError(e);
        context.removeSpiner(u);
      }).finally(() => {
        context.removeSpiner(u);
      });
    } else {
      let u = context.addSpiner();
      axios.all([
        axios.get('/api/admin/settings', {
        }),
      ]).then(axios.spread((settings) => {
        setSettings(settings.data);

        // Default "Existing patient" to "No" when the template has the field.
        if (settings.data.patientTemplate.fields.some((f) => f.code === "existingPatient")) {
          setForm({ existingPatient: "No" });
        }

        setLoading(false);
      })).catch((e) => {
        context.addError(e);
        context.removeSpiner(u);
      }).finally(() => {
        context.removeSpiner(u);
      });
    }
  }, []);

  // When alignment/tier are required, keep them populated:
  // tierAssignment → first POST_NEW/POST_EST G-code; alignmentDate → today.
  // Post-alignment: only seed tier (alignmentDate is locked).
  useEffect(() => {
    if (!settings || (!requiresTier && !requiresAlignment)) return;
    const patches = {};
    if (requiresTier && !form.tierAssignment) {
      const code = firstTierGCode(settings);
      if (code) patches.tierAssignment = code;
    }
    if (requiresAlignment && !form.alignmentDate) {
      patches.alignmentDate = formatDateYMD(new Date());
    }
    if (Object.keys(patches).length === 0) return;
    setForm((prev) => {
      const next = { ...prev };
      if (!prev.tierAssignment && patches.tierAssignment) next.tierAssignment = patches.tierAssignment;
      if (!prev.alignmentDate && patches.alignmentDate) next.alignmentDate = patches.alignmentDate;
      return next;
    });
  }, [settings, requiresTier, requiresAlignment, form.tierAssignment, form.alignmentDate]);

  // Whether the patient template has the split Physical/Mailing address
  // sections (see migration m27) so the "same as physical" checkbox can offer
  // fields to copy from.
  function hasPhysicalAddressFields() {
    return Object.keys(PHYSICAL_TO_MAILING).every(
      (code) => settings.patientTemplate.fields.some((f) => f.code === code)
    );
  }

  function isMailingPage(page) {
    return page.lines.some((line) => line.some((lineField) => lineField.code === "address"));
  }

  function toggleSameAsPhysical(checked) {
    setSameAsPhysical(checked);
    if (checked) {
      setForm(prev => {
        const next = { ...prev };
        for (const [phys, mail] of Object.entries(PHYSICAL_TO_MAILING)) {
          next[mail] = prev[phys] ? prev[phys] : null;
        }
        return next;
      });
    }
  }

  function renderPages() {
    return settings.patientTemplate.pages.map((page) => {
      return (
        <div key={page.id} className="card mb-2">
          <div className="card-header sm">
            <div className="d-flex justify-content-between align-items-center">
              <div className="typo-strong-175">{page.title}</div>
              {hasPhysicalAddressFields() && isMailingPage(page) && (
                <label className="d-flex align-items-center gap-1 typo-normal-150" style={{ cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={sameAsPhysical}
                    onChange={(e) => toggleSameAsPhysical(e.target.checked)}
                  />
                  Same as physical address
                </label>
              )}
            </div>
          </div>
          <div className="card-content sm">
            {page.lines.map((line, index) => renderLine(page.id, line, index, page.lines.length === index + 1))}
          </div>
        </div>
      );
    });
  }

  function paafCtx() {
    return { lifecycleStage, patientResidence: form.patientResidence };
  }

  function isEditMissing(code, { locked = false } = {}) {
    if (createMode || locked) return false;
    if (code === "alignmentDate" && alignmentDateLocked) return false;
    return isPatientFieldMissing(code, form[code], paafCtx());
  }

  function highlightClass(code, { locked = false } = {}) {
    if (formErrors[code] || isEditMissing(code, { locked })) return " " + MISSING_FIELD_CLASS;
    return "";
  }

  function inputClass(code, { locked = false } = {}) {
    return "input sm typo-normal-150 w-100" + highlightClass(code, { locked });
  }

  function renderLine(pageID, line, lineIndex, isLastLine) {
    // Line example
    //[{"code": "firstName", "size": 6}, {"code": "lastName", "size": 6}]

    const cells = line.map((lineField, index) => {
      let field = settings.patientTemplate.fields.find((f) => f.code === lineField.code);
      let locked = sameAsPhysical && MAILING_ADDRESS_CODES.includes(field.code);

      // Existing patient is a create-only field; hide it entirely in edit mode.
      if (field.code === "existingPatient" && !createMode) {
        return null;
      }

      // Create only: hide gated fields until Existing patient is Yes.
      // Edit always shows alignmentDate / icd10Code / tierAssignment / assessmentDate.
      if (EXISTING_PATIENT_GATED.includes(field.code) && createMode && form.existingPatient !== "Yes") {
        return null;
      }

      // RCC Partner ID is only relevant when the patient resides in an RCC;
      // hide it for a Private residence.
      if (field.code === "rccPartnerId" && !isRCCResidence(form.patientResidence)) {
        return null;
      }

      const fieldRequired = field.required
        || field.code === "existingPatient"
        || (requiresAlignment && field.code === "alignmentDate")
        || (requiresTier && field.code === "tierAssignment")
        || (field.code === "rccPartnerId" && isRCCResidence(form.patientResidence));
      const missing = isEditMissing(field.code, { locked });
      const radioMissing = !!highlightClass(field.code);

      return (
        <div key={"page_"+pageID+"_line_"+lineIndex+"_field_"+field.code} className={`col-xs-${lineField.size} py-0`}>
          <div className={"typo-strong-150" + (missing ? " text-red" : "")}>
            {field.title} {fieldRequired && <span className="text-red">*</span>}
          </div>

          {field.type === "text" && !field.format && field.code !== 'medicareBeneficiaryIdentifier' && !isPostalCodeField(field.code) && (
            <input type="text" placeholder="" value={form[field.code]} onChange={(e) => changeField(field.code, e.target.value)} disabled={locked} className={inputClass(field.code, { locked })} />
          )}

          {field.type === "email" && (
            <input
              type="email"
              placeholder=""
              value={form[field.code] || ""}
              onChange={(e) => changeField(field.code, e.target.value)}
              onBlur={() => {
                const val = form[field.code];
                setFormErrors(prev => ({ ...prev, [field.code]: val ? !validateEmail(val) : false }));
              }}
              className={inputClass(field.code)}
            />
          )}

          {field.code === 'medicareBeneficiaryIdentifier' && (
            <input
              type="text"
              value={form[field.code] || ""}
              onChange={(e) => changeField(field.code, sanitizeMBI(e.target.value))}
              onBlur={() => {
                const val = form[field.code];
                setFormErrors(prev => ({ ...prev, [field.code]: val ? !isValidMBI(val) : false }));
              }}
              className={inputClass(field.code)}
            />
          )}

          {isPostalCodeField(field.code) && (
            <>
              <input
                type="text"
                inputMode="numeric"
                placeholder="000000000"
                value={form[field.code] || ""}
                onChange={(e) => changeField(field.code, sanitizeZip(e.target.value))}
                onBlur={() => {
                  const val = form[field.code];
                  setFormErrors(prev => ({ ...prev, [field.code]: val ? !isAcceptablePatientZip(val) : false }));
                }}
                disabled={locked}
                className={inputClass(field.code, { locked })}
              />
              <div className="typo-normal-125 text-muted">{ZIP_HINT}</div>
            </>
          )}

          {field.type === "text" && field.format && field.code !== 'medicareBeneficiaryIdentifier' && !isPostalCodeField(field.code) && (
            <MaskedInput
              value={form[field.code]}
              onChange={(value) => changeField(field.code, value)}
              formatRegex={field.format}
              disabled={locked}
              className={inputClass(field.code, { locked })}
            />
          )}

          {field.type === "date" && (
            <EditableDatePicker 
              date={form[field.code]}
              onChange={(e) => {
                // Locked or required alignment date cannot be cleared.
                if (field.code === "alignmentDate" && (alignmentDateLocked || (requiresAlignment && !e))) return;
                changeField(field.code, e);
              }}
              clearable={!(field.code === "alignmentDate" && (alignmentDateLocked || requiresAlignment))}
              disabled={field.code === "alignmentDate" && alignmentDateLocked}
              maxDate={(field.code === "alignmentDate" || field.code === "assessmentDate") ? new Date() : undefined}
              rightIcon={<ChevronDown height={16} width={16} color={'grey'} />}
              className={highlightClass(field.code, { locked: field.code === "alignmentDate" && alignmentDateLocked }).trim()}
            />
          )}

          {field.type === "radio" && (
            <div className={radioMissing ? MISSING_FIELD_CLASS + " rounded p-1" : undefined}>
              {field.options.map((option) => {
                const onRadioClick = () => {
                  if (field.code === "existingPatient") {
                    // Existing patient is required Yes/No — clicking the selected option must not clear to null.
                    changeField(field.code, option);
                  } else {
                    changeField(field.code, form[field.code] == option ? null : option);
                  }
                };
                return (
                  <div
                    key={"radio_"+field.code+"_"+option}
                    className={"item typo-normal-150 me-2 " + (form[field.code] == option ? "blue" : "grey")}
                    onClick={onRadioClick}
                  >{option}</div>
                );
              })}
            </div>
          )}

          {field.type === "dropdown" && (
            <select
              value={form[field.code] || ""}
              onChange={(e) => changeField(field.code, e.target.value || null)}
              className={inputClass(field.code)}
            >
              <option value="">Select...</option>
              {field.options.map((option) => (
                <option key={"dropdown_"+field.code+"_"+option} value={option}>{option}</option>
              ))}
            </select>
          )}

          {field.type === "icd10" && (
            <select
              value={form[field.code] || ""}
              onChange={(e) => changeField(field.code, e.target.value || null)}
              className={inputClass(field.code)}
            >
              <option value="">Select...</option>
              {ICD10_CODES.map((entry) => (
                <option key={"icd10_"+field.code+"_"+entry.code} value={entry.code}>{entry.code} — {entry.definition}</option>
              ))}
            </select>
          )}

          {field.type === "gcode" && (() => {
            const tierTemplates = tierTemplatesFromSettings(settings);
            const firstCode = firstTierGCode(settings);
            // Required: no empty option (fallback to first). Optional (pre-alignment): allow null.
            const value = requiresTier
              ? (form[field.code] || firstCode)
              : (form[field.code] || "");
            return (
              <select
                value={value}
                onChange={(e) => changeField(field.code, e.target.value || null)}
                className={inputClass(field.code)}
              >
                {!requiresTier && <option value="">Select...</option>}
                {tierTemplates.map((template) => {
                  const code = gcodeFromTemplateTitle(template.title);
                  if (!code) return null;
                  return (
                    <option key={"gcode_"+field.code+"_"+template.id} value={code}>{code}</option>
                  );
                })}
              </select>
            );
          })()}
        </div>
      );
    });

    // Skip rendering the row entirely when every field on the line is hidden
    // (e.g. the existingPatient-only line in edit mode, or gated lines on create
    // while Existing patient is not Yes) so no empty row/margin is left behind.
    if (cells.every((cell) => cell === null)) {
      return null;
    }

    return (
      <div key={"page_"+pageID+"_line_"+lineIndex} className={"row " + (isLastLine ? "" : "mb-2")}>
        {cells}
      </div>
    );
  }

  function validateEmail(value) {
    return /^[A-Za-z0-9._%+\-]+@[A-Za-z0-9.\-]+\.[A-Za-z]{2,}$/.test(value);
  }

  function changeField(name, value) {
    // Post-alignment: alignmentDate is locked.
    if (name === "alignmentDate" && alignmentDateLocked) {
      return;
    }
    // Required alignment date (create + Existing Yes) cannot be cleared — only changed.
    if (name === "alignmentDate" && requiresAlignment && !value) {
      return;
    }
    // Existing patient must stay Yes or No — never null.
    if (name === "existingPatient" && value !== "Yes" && value !== "No") {
      return;
    }

    // Create a new object to avoid direct mutation
    const newForm = { ...form, [name]: value ? value : null };
    if (sameAsPhysical && PHYSICAL_TO_MAILING[name]) {
      newForm[PHYSICAL_TO_MAILING[name]] = value ? value : null;
    }
    if (name === "existingPatient") {
      // Only clear gated fields in create mode — edit locks Existing patient so
      // alignmentDate / icd10Code / tierAssignment cannot be wiped by accident.
      if (createMode && value !== "Yes") {
        EXISTING_PATIENT_GATED.forEach((code) => { newForm[code] = null; });
      } else if (createMode && value === "Yes") {
        // Create + Existing Yes: seed required migration fields.
        if (!newForm.tierAssignment) {
          const code = firstTierGCode(settings);
          if (code) {
            newForm.tierAssignment = code;
          }
        }
        if (!newForm.alignmentDate) {
          newForm.alignmentDate = formatDateYMD(new Date());
        }
      }
    }
    setForm(newForm);
  }

  function validateEmailFields() {
    const emailFields = settings.patientTemplate.fields.filter(f => f.type === "email");
    const errors = {};
    for (const field of emailFields) {
      const val = form[field.code];
      if (val && !validateEmail(val)) {
        errors[field.code] = true;
      }
    }
    setFormErrors(prev => ({ ...prev, ...errors }));
    if (Object.keys(errors).length > 0) {
      context.addMessage("Please enter a valid email address.");
    }
    return Object.keys(errors).length === 0;
  }

  function validateMBIFields() {
    const mbiField = settings.patientTemplate.fields.find(f => f.code === "medicareBeneficiaryIdentifier");
    if (!mbiField) return true;
    const val = form[mbiField.code];
    if (val && !isValidMBI(val)) {
      setFormErrors(prev => ({ ...prev, [mbiField.code]: true }));
      context.addMessage("Please enter a valid Medicare Beneficiary Identifier (MBI).");
      return false;
    }
    return true;
  }

  function validatePostalCodeFields() {
    const errors = {};
    for (const code of POSTAL_CODE_FIELDS) {
      if (!settings.patientTemplate.fields.some((f) => f.code === code)) continue;
      const val = form[code];
      if (val && !isAcceptablePatientZip(val)) {
        errors[code] = true;
      }
    }
    if (Object.keys(errors).length === 0) return true;
    setFormErrors(prev => ({ ...prev, ...errors }));
    context.addMessage("Postal code must be 5 or 9 digits.");
    return false;
  }

  // Existing patient must be explicitly Yes or No (defaults to No; never null).
  function validateExistingPatientField() {
    if (!settings.patientTemplate.fields.some((f) => f.code === "existingPatient")) return true;
    if (form.existingPatient !== "Yes" && form.existingPatient !== "No") {
      context.addMessage("Please select whether this is an existing patient.");
      return false;
    }
    return true;
  }

  // Create + Existing Yes: alignmentDate required. Post-alignment / create+Existing Yes: tier required.
  // Pre-alignment: both may be null. Post-alignment alignmentDate is locked (not validated here).
  function validateAlignmentAndTierFields() {
    if (requiresAlignment && !form.alignmentDate) {
      context.addMessage("Alignment date is required.");
      return false;
    }
    if (requiresTier && !form.tierAssignment) {
      context.addMessage("Tier assignment is required.");
      return false;
    }
    return true;
  }

  function createPatient(draft) {
    if (!validateEmailFields()) return;
    if (!validateMBIFields()) return;
    if (!validatePostalCodeFields()) return;
    if (!validateExistingPatientField()) return;
    if (!validateAlignmentAndTierFields()) return;
    let u = context.addSpiner();
    const trimmedForm = normalizeFormDates(form, settings);

    axios.post('/api/admin/patients/create', {
      draft: draft,
      values: trimmedForm,
      referralID: referralID,
    }).then((response) => {
      posthog?.capture('patient_created', {
        patient_id: response.data.patient.id,
        patient_status: draft ? 'draft' : 'active',
        from_referral: !!referralID,
      });
      navigate('/patient/'+response.data.patient.id+'/card?tab=profile');
    }).catch((e) => {
      posthog?.capture('patient_create_failed', {
        patient_status: draft ? 'draft' : 'active',
        from_referral: !!referralID,
        status: e?.response?.status ?? 0,
        code: e?.response?.data?.code ?? null,
      });
      context.addError(e);
      context.removeSpiner(u);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  function updatePatient() {
    if (!validateEmailFields()) return;
    if (!validateMBIFields()) return;
    if (!validatePostalCodeFields()) return;
    if (!validateExistingPatientField()) return;
    if (!validateAlignmentAndTierFields()) return;
    let u = context.addSpiner();
    const trimmedForm = normalizeFormDates(form, settings);

    axios.post('/api/admin/patients/update', {
      id: patientID,
      values: trimmedForm,
    }).then((response) => {
      posthog?.capture('patient_updated', {
        patient_id: response.data.id,
      });
      navigate('/patient/'+response.data.id+'/card?tab=profile');
    }).catch((e) => {
      posthog?.capture('patient_update_failed', {
        patient_id: patientID,
        status: e?.response?.status ?? 0,
        code: e?.response?.data?.code ?? null,
      });
      context.addError(e);
      context.removeSpiner(u);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  if (loading) {
    return <></>;
  }

  return (
    <>
      <div className="container">
        <div className="row mb-3">
          <div className="col-xs-12">
            <div className="d-flex justify-content-start align-items-center">
              <span className="typo-hero-small">{createMode ? "New Patient" : "Edit Patient"}</span>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-xs-12">
            {renderPages()}

            <div className="d-flex justify-content-end">
              {createMode ? (
                <Button onClick={() => createPatient(false)} color="blue" text={"Create"} />
              ) : (
                <Button onClick={updatePatient} color="blue" text={"Save"} />
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default PatientCreate;
